import { ColumnsType } from "antd/lib/table";
import React, { FunctionComponent } from "react";
import { capitalizeFirstLetter, isEmptyString, isNullOrUndefined } from "../../../../common/utils";
import { formatUtcDateToLocal, parseDate } from "../../../../common/utils/TimeUtils";
import { AuthZTable } from "../../../../components/authZTable";
import { useSystemResourcesTableController } from "./controller";
import { SystemResourceTableRecord } from "./model/view-object";

const dataAvailableAtColumnToBeShownForSystemResourceTypes = ["sisense datasource", "sisense cube"];
const currentAppliedScopesColumnToBeShownForSystemResourceTypes = [
  "sisense datasource",
  "sisense cube",
];
const createdOnColumnToBeShownForSystemResourceTypes = ["sisense dashboard"];

const SystemResourcesTable: FunctionComponent<{
  assetId: string;
  selectedSystem: string;
  selectedSystemResourceType: string;
  group: string | null;
}> = (props: {
  assetId: string;
  selectedSystem: string;
  selectedSystemResourceType: string;
  group: string | null;
}) => {
  const controller = useSystemResourcesTableController(props);

  function shouldNotRenderDataAvailableAt(type: string): boolean {
    return !dataAvailableAtColumnToBeShownForSystemResourceTypes.includes(type.toLocaleLowerCase());
  }

  function shouldNotRenderCurrentAppliedScopes(type: string): boolean {
    return !currentAppliedScopesColumnToBeShownForSystemResourceTypes.includes(
      type.toLocaleLowerCase()
    );
  }

  function shouldrenderCreatedOnAt(type: string): boolean {
    return createdOnColumnToBeShownForSystemResourceTypes.includes(type.toLocaleLowerCase());
  }

  const renderDynamicColumns = (): ColumnsType => {
    const columns = controller.getDynamicColumns();
    return columns.map((column) => ({
      title: capitalizeFirstLetter(column),
      dataIndex: `attributes[${column}]`,
      key: `attributes[${column}]`,
      render: (value: string, record: SystemResourceTableRecord) => (
        <p>{record.attributes ? record.attributes[column] ?? "" : ""}</p>
      ),
      onCell: () => {
        return {
          valign: "top",
        };
      },
      width: "auto",
      onFilter: (value: string, record: SystemResourceTableRecord) => {
        return record.attributes[column]?.toString()?.toLowerCase()?.includes(value.toLowerCase());
      },
      searchEnabled: true,
      sortEnabled: true,
      sorter: {
        compare: (a: SystemResourceTableRecord, b: SystemResourceTableRecord) => {
          if (a.attributes && b.attributes) {
            if (a.attributes[column] === undefined && b.attributes[column]) {
              return -1;
            }
            if (a.attributes[column] && b.attributes[column] === undefined) {
              return 1;
            }
            if (a.attributes[column] === undefined && b.attributes[column] === undefined) {
              return 0;
            }
            return a.attributes[column].localeCompare(b.attributes[column]);
          }
          if (a.attributes === undefined && b.attributes) {
            return 1;
          }
          if (b.attributes === undefined && a.attributes) {
            return -1;
          }
          return 0;
        },
      },
    }));
  };

  const renderSystemResources = () => {
    const getColumns = () => {
      let columns = [
        {
          title: "Name",
          dataIndex: "name",
          key: "name",
          render: (value: string) => (
            <div className={"system-resource-name"}>
              <b>{value}</b>
            </div>
          ),
          onCell: () => {
            return {
              valign: "top",
            };
          },
          width: "auto",
          searchEnabled: true,
          sortEnabled: true,
        },
        ...renderDynamicColumns(),
        {
          title: "Data available at",
          dataIndex: "dataAvailableAt",
          key: "dataAvailableAt",
          render: (value: string) => <div>{capitalizeFirstLetter(value ?? "")}</div>,
          onCell: () => {
            return {
              valign: "top",
            };
          },
          width: "auto",
          searchEnabled: true,
          sortEnabled: true,
        },
        {
          title: "Current applied scope(s) in the system",
          dataIndex: "currentAppliedScopes",
          key: "currentAppliedScopes",
          onCell: () => {
            return {
              valign: "top",
            };
          },
          render: (appliedScopes: string[]) => {
            return (
              <div className="list-values">
                {appliedScopes.map((value) => (
                  <div key={value}>{value}</div>
                ))}
              </div>
            );
          },
          width: "auto",
          searchEnabled: true,
          sortEnabled: true,
        },
        {
          title: "Individual Users Added*",
          key: "individual-users-added",
          dataIndex: "individualUsersAdded",
          render: (value: String[]) => (
            <div className="list-values">
              {value.map((individualUser: String) => (
                <div key={individualUser.toString()}>{individualUser}</div>
              ))}
            </div>
          ),
          onCell: () => {
            return {
              valign: "top",
            };
          },
          width: "auto",
          searchEnabled: false,
          sortEnabled: false,
        },
        {
          title: "Groups",
          key: "groups",
          dataIndex: "groups",
          render: (value: String[]) => (
            <div className="list-values">
              {value.map((group: String) => (
                <div key={group.toString()}>{group}</div>
              ))}
            </div>
          ),
          onCell: () => {
            return {
              valign: "top",
            };
          },
          width: "auto",
          searchEnabled: false,
          sortEnabled: false,
        },
        {
          title: "Owners",
          dataIndex: "owners",
          key: "owners",
          render: (owners: string[] | null) => (
            <div className="list-values">
              {owners?.map((owner) => (
                <div>{owner}</div>
              ))}
            </div>
          ),
          onCell: () => {
            return {
              valign: "top",
            };
          },
          width: "auto",
          searchEnabled: false,
          sortEnabled: false,
        },
      ];

      if (shouldrenderCreatedOnAt(controller.getSelectedResourceType() ?? "")) {
        columns.push({
          title: "Created on",
          dataIndex: "createdOn",
          key: "createdOn",
          render: (value: string) => {
            const createdDate =
              isNullOrUndefined(value) || isEmptyString(value)
                ? ""
                : formatUtcDateToLocal(parseDate(value), "DD MMM YYYY");
            return (
              <div className="system-resource-created-on">
                <b>{createdDate}</b>
              </div>
            );
          },
          onCell: () => {
            return {
              valign: "top",
            };
          },
          width: "auto",
          searchEnabled: false,
          sortEnabled: true,
          defaultSortOrder: "descend",
          sorter: (recordA: SystemResourceTableRecord, recordB: SystemResourceTableRecord) => {
            const recordACreatedTime = recordA.createdOn;
            const recordBCreatedTime = recordB.createdOn;
            return recordACreatedTime.localeCompare(recordBCreatedTime);
          },
        });
      }

      if (shouldNotRenderDataAvailableAt(controller.getSelectedResourceType() ?? "")) {
        columns = columns.filter((column) => column.key !== "dataAvailableAt");
      }

      if (shouldNotRenderCurrentAppliedScopes(controller.getSelectedResourceType() ?? "")) {
        columns = columns.filter((column) => column.key !== "currentAppliedScopes");
      }
      return columns;
    };

    return (
      <div className="system-resource-table">
        <AuthZTable
          name={"system-resource-table"}
          id={"system-resource-table"}
          columns={getColumns()}
          dataSource={controller.filteredGystemResourceTableRecords}
          loading={controller.shouldRenderProgress()}
        />
      </div>
    );
  };

  return renderSystemResources();
};

export default SystemResourcesTable;
